import { ChangeEvent, ReactNode } from "react";
import { Box, Checkbox, Fade, FormControlLabel, Grid } from "@mui/material";
import TextFieldInput from "components/input/textField";

type CheckboxInputFieldProps = {
  label: string;
  selected: boolean;
  value: string;
  inputTitle?: string;
  placeholder: string;
  disabled: boolean;
  inputAdornment?: string;
  inputBefore?: ReactNode;
  showInput?: boolean;
  showInputWhenChecked?: boolean;
  labelWidth?: number;
  onToggle: () => void;
  onValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const CheckboxInputField = ({
  label,
  selected,
  value,
  inputTitle,
  placeholder,
  disabled,
  inputAdornment,
  inputBefore,
  showInput: showInputOverride,
  showInputWhenChecked = true,
  labelWidth,
  onToggle,
  onValueChange,
}: CheckboxInputFieldProps) => {
  const showInput = showInputOverride ?? selected === showInputWhenChecked;
  const checkbox = (
    <FormControlLabel
      control={<Checkbox checked={selected} disabled={disabled} onChange={onToggle} />}
      label={label}
      sx={{
        ml: 0,
        ...(labelWidth !== undefined && showInput && { width: labelWidth, flexShrink: 0, mr: 0 }),
      }}
    />
  );
  const numberInput = (
    <TextFieldInput
      title={inputTitle || label}
      value={value}
      handleChange={onValueChange}
      displayMode="none"
      placeholder={placeholder}
      inputAdornment={inputAdornment}
      fullWidth={true}
      disabled={disabled}
    />
  );

  return (
    <Grid
      item
      xs={12}
      sm={showInput ? (inputBefore ? 12 : 6) : "auto"}
      sx={{
        display: inputBefore ? "grid" : "flex",
        gridTemplateColumns: inputBefore && showInput
          ? { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }
          : undefined,
        alignItems: "center",
        gap: 1,
        height: inputBefore && showInput ? { xs: "auto", sm: 57 } : 57,
      }}
    >
      {inputBefore ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {checkbox}
            <Fade in={showInput} timeout={{ enter: 150, exit: 150 }} unmountOnExit>
              <Box sx={{ flex: 1, minWidth: 0 }}>{inputBefore}</Box>
            </Fade>
          </Box>
          <Fade in={showInput} timeout={{ enter: 150, exit: 150 }} unmountOnExit>
            <Box sx={{ minWidth: 0 }}>{numberInput}</Box>
          </Fade>
        </>
      ) : (
        <>
          {checkbox}
          <Fade in={showInput} timeout={{ enter: 150, exit: 150 }} unmountOnExit>
            <Box sx={{ flex: 1, minWidth: 0 }}>{numberInput}</Box>
          </Fade>
        </>
      )}
    </Grid>
  );
};

export default CheckboxInputField;
